import React, { useCallback, useContext, useEffect, useState } from "react";
import { SsdSettingsContext } from "./SsdSettingsContext";
import Box from "@amzn/meridian/box";
import Column from "@amzn/meridian/column";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import { chainWalk } from "../../../util/Util";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import Link from "@amzn/meridian/link";
import UpdateShiftModal from "./UpdateShiftModal";
import CreateShiftModal from "./CreateShiftModal";
import UpdateBreakModal from "./UpdateBreakModal";
import CreateBreakModal from "./CreateBreakModal";
import {
  MODAL_ERROR,
  MODAL_SUCCESS,
  SSD_BREAK_TYPE,
  SSD_SHIFT_NAME,
} from "../SettingsConstant";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import { updateAdminSettings } from "./SsdSettingsNetworkAPI";
import { logOperationalMetric } from "../../../overview/utils/utils";
import { metricProcessName } from "../../../util/Constants";
import { getUserLogin } from "../../../resources/EmployeeResource";
import Tab, { TabGroup } from "@amzn/meridian/tab";
import {
  DAYS_OF_WEEK,
  getLocalizedDayOfWeek,
} from "../../../util/DateTimeUtil";

const SHIFT_DISPLAY_NAME = {
  [SSD_SHIFT_NAME.SHIFT_P1]: "P1",
  [SSD_SHIFT_NAME.SHIFT_P2]: "P2",
  [SSD_SHIFT_NAME.SHIFT_P3]: "P3",
  [SSD_SHIFT_NAME.SHIFT_P4]: "P4",
  [SSD_SHIFT_NAME.SHIFT_P5]: "P5",
};

const BREAK_TYPE_DISPLAY_NAME = {
  [SSD_BREAK_TYPE.UNPAID]: "Unpaid Break",
  [SSD_BREAK_TYPE.PAID]: "Paid Break",
  [SSD_BREAK_TYPE.SOS_STANDUP]: "Standup",
  [SSD_BREAK_TYPE.SITE_SHUTDOWN]: "Daily Shutdown",
};

const DaySelector = ({ selectedDay, setSelectedDay }) => (
  <TabGroup value={selectedDay} onChange={setSelectedDay}>
    {DAYS_OF_WEEK.map((dayOfWeek) => (
      <Tab value={dayOfWeek} key={`day-of-week-${dayOfWeek}`}>
        {getLocalizedDayOfWeek(dayOfWeek, "short")}
      </Tab>
    ))}
  </TabGroup>
);

const eventsAndBreaks = () => {
  const { viewModel, dispatch } = useContext(SsdSettingsContext);

  const isNarrowWindowSite = chainWalk(
    () => viewModel.settingsData.adminSettings.narrow_windows_enabled,
    false,
  );

  const fcId = chainWalk(
    () => viewModel.settingsData.adminSettings.site_id,
    "",
  );
  const v1CombinedShifts = chainWalk(
    () => viewModel.settingsData.adminSettings.shifts,
    [],
  );
  const v1CombinedBreaks = chainWalk(
    () => viewModel.settingsData.adminSettings.break_config,
  );

  const v2DailyShifts = chainWalk(
    () => viewModel.settingsData.adminSettings.daily_shifts,
  );
  const v2DailyBreaks = chainWalk(
    () => viewModel.settingsData.adminSettings.daily_breaks,
  );

  const originalShifts = resolveDailyShifts(v1CombinedShifts, v2DailyShifts);
  const originalBreaks = resolveDailyBreaks(v1CombinedBreaks, v2DailyBreaks);

  const [selectedDay, setSelectedDay] = useState(
    DAYS_OF_WEEK[new Date().getDay()],
  );
  const [updatedShifts, setUpdatedShifts] = useState(originalShifts);
  const [updatedBreaks, setUpdatedBreaks] = useState(originalBreaks);

  const dailyShifts = updatedShifts[selectedDay];
  const dailyBreaks = updatedBreaks[selectedDay];

  useEffect(() => {
    setUpdatedShifts(JSON.parse(JSON.stringify(originalShifts)));
    setUpdatedBreaks(JSON.parse(JSON.stringify(originalBreaks)));
  }, [viewModel.settingsData.adminSettings]);

  const setUpdatedShiftsForDay = (newDailyShifts, dayOfWeek) => {
    const newShifts = { ...updatedShifts };
    newShifts[dayOfWeek] = newDailyShifts;
    setUpdatedShifts(newShifts);
  };

  const setUpdatedBreaksForDay = (newDailyBreaks) => {
    const newBreaks = { ...updatedBreaks };
    newBreaks[selectedDay] = newDailyBreaks;
    setUpdatedBreaks(newBreaks);
  };

  const onUpdateShift = (daysOfWeek, originalShift, updatedShift) => {
    const newShifts = { ...updatedShifts };

    daysOfWeek.forEach((dayOfWeek) => {
      const newShiftsForDay = [...newShifts[dayOfWeek]];
      const index = newShifts[dayOfWeek].findIndex(
        (shift) => shift.name === originalShift.name,
      );
      if (index < 0) {
        newShiftsForDay.push(updatedShift);
      }

      newShiftsForDay[index] = updatedShift;
      newShifts[dayOfWeek] = newShiftsForDay;
    });

    setUpdatedShifts(newShifts);
  };

  const onUpdateBreak = (daysOfWeek, originalBreak, updatedBreak) => {
    const newBreaks = { ...updatedBreaks };

    daysOfWeek.forEach((dayOfWeek) => {
      const newBreaksForDay = [...newBreaks[dayOfWeek]];
      const index = newBreaks[dayOfWeek].findIndex(
        (breakConfig) =>
          breakConfig.break_group_name === originalBreak.break_group_name,
      );
      if (index < 0) {
        newBreaksForDay.push(updatedBreak);
      }

      newBreaksForDay[index] = updatedBreak;
      newBreaks[dayOfWeek] = newBreaksForDay;
    });

    setUpdatedBreaks(newBreaks);
  };

  const onDeleteShift = (shiftToDelete) => {
    const newShifts = updatedShifts[selectedDay].filter(
      (shift) => shift !== shiftToDelete,
    );
    setUpdatedShiftsForDay(newShifts, selectedDay);
  };

  const onDeleteBreak = (breakConfigToDelete) => {
    setUpdatedBreaksForDay([
      ...dailyBreaks.filter(
        (breakConfig) => breakConfig !== breakConfigToDelete,
      ),
    ]);
  };

  const onRevertShifts = () => {
    setUpdatedShifts(JSON.parse(JSON.stringify(originalShifts)));
  };

  const onRevertBreaks = () => {
    setUpdatedBreaks(JSON.parse(JSON.stringify(originalBreaks)));
  };

  const onSaveShifts = () => {
    const updatedAdminSettings = {
      ...viewModel.settingsData.adminSettings,
      daily_shifts: { ...updatedShifts },
    };
    uploadAdminSettings(updatedAdminSettings);
  };

  const onSaveBreaks = () => {
    const updatedAdminSettings = {
      ...viewModel.settingsData.adminSettings,
      daily_breaks: { ...updatedBreaks },
    };
    uploadAdminSettings(updatedAdminSettings);
  };

  const uploadAdminSettings = async (
    updatedAdminSettings,
    successMessage,
    failureMessage,
  ) => {
    updatedAdminSettings.lastUpdatedBy = await getUserLogin();
    updatedAdminSettings.lastUpdatedAt = Math.round(Date.now() / 1000);
    const startTime = Date.now();
    updateAdminSettings(updatedAdminSettings)
      .then((res) => {
        if (res.status === 200 && res.statusText === "OK") {
          dispatch({
            type: "UPDATE_ADMIN_SETTINGS",
            data: {
              adminSettings: updatedAdminSettings,
              showUpdatePlanResponseModal: MODAL_SUCCESS,
              messageToDisplay: successMessage,
            },
          });
          logOperationalMetric(
            startTime,
            metricProcessName,
            "updateSsdAdminSettings",
            true,
          );
        } else {
          return Promise.reject();
        }
      })
      .catch(() => {
        dispatch({
          type: "UPDATE_ADMIN_SETTINGS",
          data: {
            adminSettings: viewModel.settingsData.adminSettings,
            showUpdatePlanResponseModal: MODAL_ERROR,
            messageToDisplay: failureMessage,
          },
        });
        logOperationalMetric(
          startTime,
          metricProcessName,
          "updateSsdAdminSettings",
          false,
        );
      });
  };

  const isSaveShiftsDisabled =
    JSON.stringify(originalShifts) === JSON.stringify(updatedShifts);
  const isSaveBreaksDisabled =
    JSON.stringify(originalBreaks) === JSON.stringify(updatedBreaks);

  return (
    <Column width="100%">
      {!isNarrowWindowSite && (
        <Box type="fill" spacingInset="400">
          <Row alignmentHorizontal="right">
            <DaySelector
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
            />
            <CreateShiftModal
              allShifts={updatedShifts}
              dayOfWeek={selectedDay}
              onSubmit={onUpdateShift}
            />
            <Link
              type={"secondary"}
              onClick={onRevertShifts}
              disabled={isSaveShiftsDisabled}
            >
              Cancel
            </Link>
            <Button
              onClick={onSaveShifts}
              type="primary"
              disabled={isSaveShiftsDisabled}
            >
              Save
            </Button>
          </Row>
        </Box>
      )}
      {!isNarrowWindowSite && (
        <Table
          headerRows={1}
          showDividers={true}
          showStripes={false}
          spacing="small"
        >
          <TableRow highlightOnHover={true}>
            <TableCell>Shift</TableCell>
            <TableCell>Primary CPT</TableCell>
            <TableCell>First Half Time</TableCell>
            <TableCell>Back Half Time</TableCell>
            <TableCell>Lower OB TPH Goal (1H/2H)</TableCell>
            <TableCell>Upper OB TPH Goal (1H/2H)</TableCell>
            <TableCell>Lower Sort TPH Goal (1H/2H)</TableCell>
            <TableCell>Upper Sort TPH Goal (1H/2H)</TableCell>
            <TableCell />
          </TableRow>
          {dailyShifts.map((shift, index) => (
            <TableRow key={`shift-${index}`}>
              <TableCell>
                {chainWalk(() => SHIFT_DISPLAY_NAME[shift.name], "")}
              </TableCell>
              <TableCell>
                {chainWalk(() => shift.primary_cpt.slice(0, -3), "")}
              </TableCell>
              <TableCell>
                {chainWalk(
                  () =>
                    shift.first_half_start.slice(0, -3) +
                    "-" +
                    shift.first_half_end.slice(0, -3),
                  "",
                )}
              </TableCell>
              <TableCell>
                {chainWalk(
                  () =>
                    shift.back_half_start.slice(0, -3) +
                    "-" +
                    shift.back_half_end.slice(0, -3),
                  "",
                )}
              </TableCell>
              <TableCell>
                {chainWalk(() => shift.first_half_lower_outbound_tph_goal, "")}/
                {chainWalk(() => shift.back_half_lower_outbound_tph_goal, "")}
              </TableCell>
              <TableCell>
                {chainWalk(() => shift.first_half_upper_outbound_tph_goal, "")}/
                {chainWalk(() => shift.back_half_upper_outbound_tph_goal, "")}
              </TableCell>
              <TableCell>
                {chainWalk(() => shift.first_half_lower_sortation_tph_goal, "")}
                /{chainWalk(() => shift.back_half_lower_sortation_tph_goal, "")}
              </TableCell>
              <TableCell>
                {chainWalk(() => shift.first_half_upper_sortation_tph_goal, "")}
                /{chainWalk(() => shift.back_half_upper_sortation_tph_goal, "")}
              </TableCell>
              <TableCell>
                <Row>
                  <UpdateShiftModal
                    allShifts={updatedShifts}
                    shift={shift}
                    dayOfWeek={selectedDay}
                    onSubmit={onUpdateShift}
                  />
                  &nbsp;
                  <DeleteShiftModal shift={shift} onDelete={onDeleteShift} />
                </Row>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      )}
      <Box type="fill" spacingInset="400">
        <Row alignmentHorizontal="right">
          {isNarrowWindowSite ? (
            <DaySelector
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
            />
          ) : (
            <Box width="100%">
              <Text type="h100">{getLocalizedDayOfWeek(selectedDay)}</Text>
            </Box>
          )}
          <CreateBreakModal
            allBreaks={updatedBreaks}
            fcId={fcId}
            onSubmit={onUpdateBreak}
            breakConfigs={dailyBreaks}
          />
          <Link
            type={"secondary"}
            onClick={onRevertBreaks}
            disabled={isSaveBreaksDisabled}
          >
            Cancel
          </Link>
          <Button
            onClick={onSaveBreaks}
            type="primary"
            disabled={isSaveBreaksDisabled}
          >
            Save
          </Button>
        </Row>
      </Box>
      <Table
        headerRows={1}
        showDividers={true}
        showStripes={false}
        spacing="small"
      >
        <TableRow highlightOnHover={true}>
          <TableCell>Event Category</TableCell>
          <TableCell>Start Time</TableCell>
          <TableCell>End Time</TableCell>
          <TableCell />
        </TableRow>
        {dailyBreaks.map((breakConfig, index) => (
          <TableRow key={`break-${index}`}>
            <TableCell>
              {BREAK_TYPE_DISPLAY_NAME[breakConfig.break_type]}
            </TableCell>
            <TableCell>{breakConfig.break_start_time_local}</TableCell>
            <TableCell>{breakConfig.break_end_time_local}</TableCell>
            <TableCell>
              <Row>
                <UpdateBreakModal
                  allBreaks={updatedBreaks}
                  breakConfig={breakConfig}
                  onSubmit={onUpdateBreak}
                  breakConfigs={dailyBreaks}
                />
                <DeleteBreakModal
                  breakConfig={breakConfig}
                  onDelete={onDeleteBreak}
                />
              </Row>
            </TableCell>
          </TableRow>
        ))}
      </Table>
    </Column>
  );
};

const DeleteShiftModal = ({ shift, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const onConfirm = useCallback(() => {
    onDelete(shift);
    setIsOpen(false);
  }, [shift, onDelete]);
  return (
    <React.Fragment>
      <Button type="icon" onClick={() => setIsOpen(true)}>
        <Text color="error">
          <Icon tokens={trashTokens} />
        </Text>
      </Button>
      <Modal
        title="Delete Shift"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        scrollContainer="viewport"
        closeLabel="Close"
      >
        <Text id="modal-description">
          Are you sure you want to delete the shift for{" "}
          {chainWalk(() => SHIFT_DISPLAY_NAME[shift.name], shift.name)}?
        </Text>
        <ModalFooter>
          <Row alignmentHorizontal="right" widths="fit">
            <Button
              type="secondary"
              size="small"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button type="primary" size="small" onClick={onConfirm}>
              Confirm
            </Button>
          </Row>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

const DeleteBreakModal = ({ breakConfig, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const onConfirm = useCallback(() => {
    onDelete(breakConfig);
    setIsOpen(false);
  }, [breakConfig, onDelete]);
  return (
    <React.Fragment>
      <Button type="icon" onClick={() => setIsOpen(true)}>
        <Text color="error">
          <Icon tokens={trashTokens} />
        </Text>
      </Button>
      <Modal
        title="Delete Event"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        scrollContainer="viewport"
        closeLabel="Close"
      >
        <Text id="modal-description">
          Are you sure you want to delete the event for{" "}
          {breakConfig.break_group_name}?
        </Text>
        <ModalFooter>
          <Row alignmentHorizontal="right" widths="fit">
            <Button
              type="secondary"
              size="small"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button type="primary" size="small" onClick={onConfirm}>
              Confirm
            </Button>
          </Row>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

const resolveDailyShifts = (combinedShifts, dailyShifts) => {
  const convertedShifts = dailyShifts ? dailyShifts : {};
  DAYS_OF_WEEK.forEach((dayOfWeek) => {
    if (!dailyShifts) {
      convertedShifts[dayOfWeek] = [...combinedShifts];
    } else if (!dailyShifts[dayOfWeek]) {
      convertedShifts[dayOfWeek] = [];
    }
  });

  return convertedShifts;
};

const resolveDailyBreaks = (combinedBreaks, dailyBreaks) => {
  const convertedBreaks = dailyBreaks ? dailyBreaks : {};
  if (!dailyBreaks) {
    DAYS_OF_WEEK.forEach((dayOfWeek) => {
      convertedBreaks[dayOfWeek] = [...combinedBreaks];
    });
  }

  return convertedBreaks;
};

export default eventsAndBreaks;



// WEBPACK FOOTER //
// ./src/components/settings/settingsv2/ssd/EventsAndBreaks.js